import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RecordReviewContext } from 'src/app/modules/tasks/record-review';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { NewSupplier } from '../../models/supplier/new-supplier';
import { BankAccount } from '../../models/account/bank-account';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';
import { SharedService } from '../../services/shared-service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { CurrencyCode } from '../../enums/currency-code';
import { PaymentAccount } from '../../models/account/payment-account';
import { EnumItem } from '../../models/enum-item';
import { enumNumberSelector } from 'src/app/core/util/enum-selector';
import { PaymentMethod } from '../../enums/payment-method';
import { fromCamelCase } from 'src/app/core/util/camel-case-converter';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { take } from 'rxjs/operators';
import { BankAccountService } from 'src/app/data/service/bank-account.service';
import { RecognizedSupplierService } from 'src/app/modules/payable/services/recognized-supplier.service';
import { BillsToPayInvoiceDetailsRequest, BillsToPaySupplierDetailsRequest } from '../../models/invoice/bills-to-pay-invoice-details-request';
import { RecognizedInvoiceService } from '../../services/recognized-invoice.service';
import { forkJoin } from 'rxjs';
import { TaxRate } from '../../models/taxRate';
import { Guid } from "guid-typescript";
import { ConfirmationDialogService } from 'src/app/shared/confirmation-dialog/confirmation-dialog.service';

enum Pannels {
  DetailsTab,
  ActivityTab,
  RelatedTab,
  SplitLinesPanel,
  ContactForm,
  ExpensesTab,
  InvoiceDetailsTab
}

@Component({
  selector: 'app-supplier-contancts',
  templateUrl: './supplier-contancts.component.html',
  styleUrls: ['./supplier-contancts.component.scss'],
  // The approval open on this supplier, shared by its approval block and every [recordField].
  providers: [RecordReviewContext]
})
export class SupplierContanctsComponent extends BaseComponent implements OnInit {
  @Input() currentPannel: Pannels;
  @Input() supplier: NewSupplier;
  /** An approval block is showing at the foot of the panel. */
  approvalActive = false;
  private _reviewKey: string = null;
  private _reviewIds: string[] = [];

  /** The supplier's id, for its approval block; stable so the block does not reload each check. */
  get reviewRecordIds(): string[] {
    const key = this.supplier?.supplierId || '';
    if (key !== this._reviewKey) {
      this._reviewKey = key;
      this._reviewIds = key ? [key] : [];
    }
    return this._reviewIds;
  }
  @Input() accounts: PaymentAccount[] = [];
  @Input() account: BankAccount;
  @Input() billsToPayMode: boolean = false;
  @Input() supplierTabMode: boolean = false;
  @Input() isEditable: boolean = false;
  @Input() invoiceColorNumber: number = 1;
  @Output() isClosed = new EventEmitter<boolean>();
  @Output() newSupplier = new EventEmitter<NewSupplier>();
  @Output() closeFormEvent = new EventEmitter<string>();
  @Output() contactIsSavedEvent = new EventEmitter<boolean>();

  private readonly bsbPattern = /\d{3}-\d{3}/i;
  private readonly routingPattern = /\d{9}/i;
  private readonly accountNumberPattern = /^[0-9]*$/i;

  showToolTipDelay: number = 400;
  suppliersGroup: any = [];
  bankAccount: BankAccount = new BankAccount(this._organizationService.selectedOrganization);
  isPaymentAccountShown: boolean = true;
  bankName: string = '';
  defaultCurrency: CurrencyCode;
  isVisible: boolean = false;
  dropdownList = [];
  selectedItems = [];
  dropdownSettings = {};
  loading = false;
  selectedGroupId = [];
  paymentMethods: EnumItem[] = enumNumberSelector(PaymentMethod, fromCamelCase);
  panels = Pannels;
  currencies: string[] = [];
  taxRates: TaxRate[] = [];
  countryCode: string = "AU";

  // Track which fields are being edited
  editingField: {
    name?: boolean;
    taxNumber?: boolean;
    groups?: boolean;
    defaultAccount?: boolean;
    defaultTaxRate?: boolean;
    currency?: boolean;
    firstName?: boolean;
    lastName?: boolean;
    email?: boolean;
    bsb?: boolean;
    accountNumber?: boolean;
    routingNumber?: boolean;
    accountNumberUS?: boolean;
    alias?: boolean;
  } = {};

  // Track which alias is being edited
  editingAliasIndex: number = -1;
  editingAliasValue: string = '';

  // New alias being added
  newAlias: string = '';
  /** The new-alias field shows only once "Add alias" is pressed. */
  addingAlias = false;
  /** Contact and payment details show as a summary; Edit (or Add) opens their fields. */
  editingContact = false;
  editingPayment = false;

  /** Tax rate picker (supplierTabMode): the supplier's rate is held by name. */
  readonly isTaxRateNamed = (rate: TaxRate, name: string): boolean => rate?.name === name;

  constructor(
    private _suppliersService: SuppliersService,
    private _organizationService: OrganizationService,
    private _toasterMessageService: ToasterMessageService,
    private _sharedService: SharedService,
    public spinnerService: SpinnerService,
    private _invoiceService: InvoiceService,
    private _bankAccountService: BankAccountService,
    private readonly _supplierRecognizeService: RecognizedSupplierService,
    private readonly _recognizedInvoiceService: RecognizedInvoiceService,
    private confirmationDialog: ConfirmationDialogService
  ) {
    super();
  }

  ngOnInit() {
    this._organizationService.getOrganizationCountry(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.countryCode = response.countryCode;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
    if (this.supplierTabMode) {
      this._supplierRecognizeService.supplierContactFormSubject$
        .pipe(this.untilThis)
        .subscribe(
          (request: BillsToPaySupplierDetailsRequest) => {
            this.invoiceColorNumber = request.invoiceColorNumber;
            this.getAccounts();
            this.supplier = new NewSupplier(this._organizationService.selectedOrganization);
            this.supplier.supplierId = request.supplierId;
            this.getAllInitialData();
          }
        );
    } else {
      this.getAllInitialData();
    }
    if (this.currentPannel) {
      this.tabClick(this.currentPannel);
    }
    else {
      // Open on the Expenses tab whenever we have an existing contact to show -
      // the dedicated contacts side panel (supplierTabMode) and the contact opened
      // from the Inbox detail view (existing supplierId). A brand-new supplier being
      // created inline (no supplierId yet) still opens on Details.
      this.currentPannel = (this.supplierTabMode || !!this.supplier?.supplierId)
        ? Pannels.ExpensesTab
        : Pannels.DetailsTab;
    }
  }

  /** The side panel header's second line: the contact person and their email. */
  get contactSummary(): string {
    const name = [this.supplier?.firstName, this.supplier?.lastName]
      .map(part => (part || '').trim())
      .filter(part => part)
      .join(' ');
    return [name, (this.supplier?.email || '').trim()].filter(part => part).join(' · ');
  }

  clickOnRelatedInvoice(invoiceId: string) {
    if (this.billsToPayMode) {
      // Bills-to-pay hosts already load the bill into the parent invoice form.
      this.closeFormOnSupplier(invoiceId);
    } else {
      // Standalone contact panel (Suppliers list, global bar, Inbox contact view):
      // open the bill detail in place, like the Company opens within the Inbox detail.
      this.openInvoiceDetail(invoiceId);
    }
  }

  openInvoiceDetail(invoiceId: string) {
    this.currentPannel = Pannels.InvoiceDetailsTab;

    const request = {
      invoiceId,
      organizationId: this._organizationService.selectedOrganization,
      supplierId: null,
      reportId: null,
      invoiceColorNumber: this.invoiceColorNumber,
      returnBackToText: 'Supplier'
    } as BillsToPayInvoiceDetailsRequest;

    // The detail form subscribes to the request on init, so let *ngIf instantiate
    // it before we send (matches the delay used when opening it from Bills to Pay).
    setTimeout(() => {
      this._recognizedInvoiceService.sendBillsToPayInvoiceDetailsRequest(request);
    }, 300);
  }

  backToExpenses() {
    this.currentPannel = Pannels.ExpensesTab;
  }

  closeFormOnSupplier(invoiceId: string = null) {
    this.closeFormEvent.emit(invoiceId);
  }

  checkBSB() {
    if (!!this.bankAccount?.branchDetails && !this.bankAccount?.branchDetails.includes('-'))
      this.bankAccount.branchDetails = this.bankAccount.branchDetails?.slice(0, 3) + '-' + this.bankAccount.branchDetails?.slice(3);
  }

  subscribeToAccountUpdates() {
    this._bankAccountService.accountState$
      .pipe(this.untilThis)
      .subscribe(
        (account: BankAccount) => {
          this.bankAccount = account;
          this.checkBSB();
        }
      );
    if (this.account) {
      this._bankAccountService.updateBankAccount(this.account);
    }
  }
  onDeleteGroup(item: any) {
    this.selectedGroupId = Object.values(this.selectedGroupId).filter(g => g != item.typeId);
    this.saveContactForm('Contact Group');
  }

  onItemSelect(item: any) {
    this.selectedItems.push(item);
    this.selectedGroupId.push(item.typeId);
    this.saveContactForm('Contact Group')
  }

  /** Clearing every group at once from the group picker (supplierTabMode). */
  onClearGroups() {
    this.selectedItems = [];
    this.selectedGroupId = [];
    this.saveContactForm('Contact Group');
  }

  onSelectAll(items: any) {
    this.selectedItems = items;
    items.forEach(element => {
      this.selectedGroupId.push(element.typeId);
    });

    this.saveContactForm('Contact Group')
  }

  submit(fieldName: string) {
    this.supplier.bsb = this.bankAccount.branchDetails ? this.bankAccount.branchDetails : this.supplier.bsb;
    this.supplier.accountNumber = this.bankAccount.accountNumber ? this.bankAccount.accountNumber : this.supplier.accountNumber;
    this.supplier.routingNumber = this.bankAccount.routingNumber ? this.bankAccount.routingNumber : this.supplier.routingNumber;
    this.supplier.accountNumberUS = this.bankAccount.accountNumberUS ? this.bankAccount.accountNumberUS : this.supplier.accountNumberUS;

    this.addNewSupplier(this.supplier, fieldName)
  }

  addNewSupplier(supplier: NewSupplier, fieldName: string = null) {
    this.loading = true;
    this.contactIsSavedEvent.emit(true);
    if (supplier?.bsb && supplier?.accountNumber) {
      supplier.bankAccountRequestModel.BSBNumber = supplier.bsb?.replace('-', '');
      supplier.bankAccountRequestModel.accountNumber = supplier.accountNumber;
      supplier.bankAccountRequestModel.branchDetails = supplier.bankAccountRequestModel.BSBNumber;
    }
    if (supplier?.routingNumber && supplier?.accountNumberUS) {
      supplier.bankAccountRequestModel.accountNumberUS = supplier.accountNumberUS;
      supplier.bankAccountRequestModel.routingNumber = this.bankAccount.routingNumber;
    }

    const newSupplier = JSON.parse(JSON.stringify(supplier));
    newSupplier.bsb = newSupplier.bsb.replace('-', '');
    if (!newSupplier.defaultAccount.id) newSupplier.defaultAccount = null;
    newSupplier.GroupId = this.selectedGroupId;
    if (this.supplier.supplierId != "") {
      newSupplier.id = this.supplier.supplierId;
      this.loading = true;

      this._suppliersService.updateSupplier(newSupplier)
        .pipe(this.untilThis)
        .subscribe(
          (result: any) => {
            this.loading = false;
            let domen = this._organizationService.selectedOrganizationDomen;

            if (result?.error && result?.error == 'ConnectionLost') {
              if (domen == 'QBO') {
                this._organizationService.getIntegrationCredentials('qbo').subscribe((res) => {
                  if (res) {
                    this._organizationService.refreshConnectionURL = res.revokeEndPoint;
                    this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
                      `QuickBooks connection lost. Please <a  class=\"toastr-link\" href=\"${this._organizationService.refreshConnectionURL}\">refresh</a> connection`);
                  }
                });
              } else {
                this._organizationService.getIntegrationCredentials('xero').subscribe((res) => {
                  if (res) {
                    this._organizationService.refreshConnectionURL = res.revokeEndPoint;
                    this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
                      `Xero connection lost. Please <a  class=\"toastr-link\" href=\"${this._organizationService.refreshConnectionURL}\">refresh</a> connection`);
                  }
                });
              }
            } else {
              // Only show the specific field that was updated
              this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', `${fieldName} Updated!`);
            }
          }
        )
    }
  }

  updateBankName(name: string) {
    this.bankName = name;
  }

  /** SD-1240: the master contact's logo for this supplier, if it has one; initials otherwise. */
  supplierLogoUrl: string = null;

  private loadMasterContactLogo(): void {
    this.supplierLogoUrl = null;
    const org = this._organizationService.selectedOrganization;
    const id = (this.supplier?.supplierId || '').toLowerCase();
    if (!org || !id) return;
    this._suppliersService.getMasterContactLinks(org).subscribe(
      links => {
        const hit = (links || []).find(l => (l.supplierId || '').toLowerCase() === id);
        this.supplierLogoUrl = hit?.logoUrl || null;
      },
      () => { /* decoration only */ });
  }

  getSupplierGroup() {
    const groups$ = this._suppliersService.GetSupplierGroup(this._organizationService.selectedOrganization);
    const supplier$ = this._suppliersService.getSupplierById(this._organizationService.selectedOrganization, this.supplier.supplierId);
    const defaultCurrency$ = this._organizationService.getOrganizationDefaultCurrency(this._organizationService.selectedOrganization);

    const forkJoin$ = forkJoin({
      groups: groups$,
      supplier: supplier$,
      defaultCurrency: defaultCurrency$,
    });

    forkJoin$.subscribe(
      (forkData) => {
        this.suppliersGroup = forkData?.groups?.data || [];
        this.defaultCurrency = forkData?.defaultCurrency.currency;
        this.loadMasterContactLogo();

        if (forkData.supplier?.bsb) {
          this.supplier.bsb = forkData.supplier?.bsb.slice(0, 3) + "-" + forkData.supplier?.bsb.slice(3);
          this.bankAccount.branchDetails = this.supplier.bsb;
          this.bankAccount.accountNumber = forkData.supplier?.accountNumber;
        }
        if (forkData.supplier?.routingNumber) {
          this.bankAccount.routingNumber = forkData.supplier?.routingNumber;
          this.bankAccount.accountNumberUS = forkData.supplier?.accountNumberUS;
        }

        this.supplier.currency = forkData.supplier?.currency && forkData.supplier?.currency?.toString() != '0' ? forkData.supplier?.currency : this.defaultCurrency;
        this.supplier.name = forkData.supplier?.name;
        this.supplier.firstName = forkData.supplier?.firstName;
        this.supplier.lastName = forkData.supplier?.lastName;
        this.supplier.taxNumber = forkData.supplier?.taxNumber;
        this.supplier.accountName = forkData.supplier?.accountName;
        this.supplier.email = forkData.supplier?.email;
        this.supplier.aliases = forkData.supplier?.aliases;
        this.supplier.hexColorClass = forkData.supplier?.hexColorClass;

        if (forkData.supplier?.contactGroups != null && forkData.supplier?.contactGroups.length > 0) {
          let itemArray = [];
          var contactGroupsArr = forkData.supplier.contactGroups.split(',');
          contactGroupsArr.forEach(element => {
            if (element != "") {
              let cntGrp = this.suppliersGroup.find(x => x.typeId == element.toLocaleLowerCase());
              itemArray.push(cntGrp);
            }
          });
          this.selectedItems = itemArray;
          this.selectedGroupId = itemArray.map(g => g.typeId);
        }

        if (forkData.supplier?.defaultAccount != null) {
          let defAcc = this.accounts.find(x => x.code.toLowerCase() == forkData.supplier?.defaultAccount.toString().toLowerCase());

          if (defAcc)
            this.supplier.defaultAccount = defAcc;
        }
        if (forkData.supplier?.accountsPayableTaxType != null) {
          let defRate = this.taxRates.find(x => x.taxType.toLowerCase() == forkData.supplier?.accountsPayableTaxType.toString().toLowerCase());
          if (defRate) {
            this.supplier.accountsPayableTaxType = defRate.taxType;
            this.supplier.accountsPayableTaxName = defRate.name;
          }
        }
      }
    );
  }

  doesFileExist(urlToFile) {
    var xhr = new XMLHttpRequest();
    xhr.open('HEAD', urlToFile, false);
    try {
      xhr.send();
      if (xhr.status == 404) {
        return false;
      } else {
        return true;
      }
    }
    catch {
      return false;
    }
  }

  onSelectAccount(event: TypeaheadMatch) {
    this.supplier.defaultAccount = event.item;
    this.saveContactForm('Default Account')
  }

  onSelectTaxRate(event: TypeaheadMatch) {
    this.supplier.accountsPayableTaxName = event.item.name;
    this.supplier.accountsPayableTaxType = event.item.taxType;
    this.saveContactForm('Default Tax Rate')
  }

  cancel() {
    this.isClosed.emit(false);
    this._sharedService.closeActionBar(false);
  }

  togglePaymentAccount() {
    this.isPaymentAccountShown = !this.isPaymentAccountShown;
  }

  updateBSB(bsb: string) {
    this.bankAccount.branchDetails = bsb.length === 3 ? bsb + '-' : bsb;

    this.getBankName(bsb);
  }

  updateRouting(routingNumber: string) {
    this.bankAccount.routingNumber = routingNumber;
  }

  getBankName(bsb: string) {
    if (bsb) {
      if (!!bsb.match(this.bsbPattern)) {
        this._invoiceService.getBankNameByBSB(bsb)
          .pipe(take(1))
          .subscribe(
            (result) => {
              this.bankName = result.name || '';
            }
          )
      } else {
        this.bankName = '';
      }
    }
  }

  tabClick(tab: Pannels) {
    switch (tab) {
      case Pannels.ExpensesTab:
        this.currentPannel = Pannels.ExpensesTab;
        break;
      case Pannels.DetailsTab:
        this.currentPannel = Pannels.DetailsTab;
        break;
      case Pannels.ActivityTab:
        this.currentPannel = Pannels.ActivityTab;
        break;
      case Pannels.RelatedTab:
        this.currentPannel = Pannels.RelatedTab;
        break;
      default:
        this.currentPannel = Pannels.DetailsTab;
        break;
    }
  }

  saveContactForm(fieldName: string) {
    if (fieldName == 'Name') {
      this.supplier.name = this.supplier.name.trim();
      if (this.supplier.name) {
        this._suppliersService.checkSupplierNameExists(this._organizationService.selectedOrganization, this.supplier.name, this.supplier.supplierId)
          .pipe(take(1))
          .subscribe(
            (exists) => {
              if (exists.status == 1) {
                this.confirmationDialog
                  .confirm(
                    'Merge Contacts',
                    `Supplier with the same name already exists. Do you want to merge these contacts?`,
                    'Merge Contacts',
                    'Cancel',
                    null,
                    'md'
                  )
                  .then(confirmed => {
                    if (confirmed) {
                      this._suppliersService.mergeSuppliers(this._organizationService.selectedOrganization, this.supplier.supplierId, exists.data)
                        .pipe(take(1))
                        .subscribe(
                          () => {
                            this.submit(fieldName);
                            this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Suppliers merged successfully');
                          },
                          (e: Error) => {
                            this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
                          }
                        );
                    }
                    else{
                      this.submit(fieldName);
                    }
                  })
                  .catch(() => { });
              } else if (this.isEditable) {
                this.submit(fieldName);
              }
            },
            (e: Error) => {
              this._toasterMessageService.displayToasterState(
                ToasterTypes.Error,
                'Error',
                e.message
              );
            }
          );
        return; // Prevent submit until check completes
      }
    }
    if (this.isEditable) {
      this.submit(fieldName);
    }
  }

  saveBSBAccountPair() {
    if (this.bankAccount?.branchDetails && this.bankAccount?.accountNumber
      && this.bsbPattern.test(this.bankAccount?.branchDetails) && this.accountNumberPattern.test(this.bankAccount?.accountNumber)) {
      this.saveContactForm('BSB and Account Number');
    }
  }

  saveRoutingAccountPair() {
    if (this.bankAccount?.routingNumber && this.bankAccount?.accountNumberUS
      && this.routingPattern.test(this.bankAccount?.routingNumber) && this.accountNumberPattern.test(this.bankAccount?.accountNumberUS)) {
      this.saveContactForm('Routing Number and Account Number');
    }
  }

  showActivityPanel() {
    if (this.currentPannel == Pannels.ActivityTab) {
      this.currentPannel = Pannels.DetailsTab;
    } else {
      this.currentPannel = Pannels.ActivityTab;
    }
  }

  private getAccounts() {
    this._bankAccountService.getPaymentAccounts(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (accounts: PaymentAccount[]) => {
          this.accounts = accounts;
        });
  }

  private getAllInitialData() {
    this.dropdownSettings = {
      singleSelection: false,
      idField: 'typeId',
      textField: 'typeName',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 3,
      allowSearchFilter: true
    };
    this.getSupplierGroup();
    this.getOrganizationCurrencies();
    this.checkBSB();
    this.getBankName(this.bankAccount.branchDetails);
    this.subscribeToAccountUpdates();
    this.getTaxRates()
  }

  private getOrganizationCurrencies() {
    this._organizationService.getOrganizationCurrencies(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (data: string[]) => {
          this.currencies = [...data];
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  getTaxRates() {
    this._organizationService.getTaxRates(this._organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (taxRates: TaxRate[]) => {
          this.taxRates = [...taxRates];
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }
  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  startAddingAlias(): void {
    this.addingAlias = true;
    setTimeout(() => (document.getElementById('sc-new-alias') as HTMLInputElement)?.focus());
  }

  cancelAddingAlias(): void {
    this.addingAlias = false;
    this.newAlias = '';
  }

  /** The contact person as one line: "First Last". */
  get contactPersonName(): string {
    return [this.supplier?.firstName, this.supplier?.lastName].filter(v => v && v.trim()).join(' ');
  }

  get hasContactDetails(): boolean {
    return !!(this.contactPersonName || (this.supplier?.email || '').trim());
  }

  get hasPaymentDetails(): boolean {
    return this.countryCode == 'US'
      ? !!(this.bankAccount?.routingNumber || this.bankAccount?.accountNumberUS)
      : !!(this.bankAccount?.branchDetails || this.bankAccount?.accountNumber);
  }

  /** An account number with all but its last four digits hidden. */
  maskedAccount(value: string): string {
    const v = (value || '').toString().replace(/\s/g, '');
    return v.length > 4 ? '•••• ' + v.slice(-4) : v;
  }

  addAlias() {
    if (this.newAlias?.trim()) {
      if (!this.supplier.aliases) {
        this.supplier.aliases = [];
      }
      const newAliasObj = {
        id: Guid.create().toString(),
        organizationId: this._organizationService.selectedOrganization,
        supplierId: this.supplier.supplierId,
        alias: this.newAlias.trim()
      };

      this._suppliersService.saveSupplierAlias(newAliasObj)
        .pipe(take(1))
        .subscribe(
          () => {
            this.supplier.aliases.push(newAliasObj);
            this.newAlias = '';
            this.addingAlias = false;
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }
  }

  removeAlias(index: number) {
    if (this.supplier.aliases && index >= 0 && index < this.supplier.aliases.length) {
      this._suppliersService.deleteSupplierAlias(this.supplier.aliases[index].id)
        .pipe(take(1))
        .subscribe(
          () => {
            this.supplier.aliases.splice(index, 1);
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }
  }

  startEditingAlias(index: number) {
    this.editingAliasIndex = index;
    this.editingAliasValue = this.supplier.aliases[index].alias;
  }

  saveEditedAlias() {
    if (this.editingAliasIndex >= 0 && this.editingAliasValue?.trim()) {
      this.supplier.aliases[this.editingAliasIndex].alias = this.editingAliasValue.trim();
      this._suppliersService.saveSupplierAlias({
        id: this.supplier.aliases[this.editingAliasIndex].id || Guid.create().toString(),
        organizationId: this._organizationService.selectedOrganization,
        supplierId: this.supplier.supplierId,
        alias: this.supplier.aliases[this.editingAliasIndex].alias,
      })
        .pipe(take(1))
        .subscribe(
          () => {
            this.editingAliasIndex = -1;
            this.editingAliasValue = '';
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }
  }

  cancelEditingAlias() {
    this.editingAliasIndex = -1;
    this.editingAliasValue = '';
  }

  handleAliasKeypress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEditedAlias();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditingAlias();
    }
  }
}
